/**
 * The socket to the arena.
 *
 * Deliberately much smaller than `live.ts`: an arena has no ground, so there are no positions, no
 * interpolation, no roster to smooth and no anchor to translate. What is left is a connection, a
 * backoff, and a stream of things that happened to one duel.
 *
 * It is a nudge, never the truth. Every event here is also a row in D1 that the next state response
 * would have carried anyway; what the socket buys is that a vote lands as a move rather than as a
 * number that was already different when you looked. Losing the socket entirely costs nothing but
 * the animation.
 *
 * Written against the same two rules as `core/soba.ts`: an unrecognised kind is ignored, and the
 * room name carries its kind.
 */

import { SOBA_VERSION, decodeFromRoom, type DuelEvent, type KrajReason } from '../../core/soba';

const BACKOFF_MIN_MS = 700;
const BACKOFF_MAX_MS = 20_000;

export interface ArenaHooks {
  /** Something happened to the duel. `side` and `axis` are set only for a vote. */
  onEvent(ev: DuelEvent, side?: 0 | 1, axis?: string): void;
  /** The other fighter opened the arena, or closed it. An event, never a light that stays on. */
  onPresence(name: string, here: boolean): void;
}

export class Arena {
  private socket: WebSocket | null = null;
  private duelId: string | null = null;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Set by a refusal that will still be a refusal next time. Never retried. */
  private blocked = false;
  /** Set by a version mismatch. Permanent until the page reloads. */
  private stale = false;

  constructor(
    private readonly origin: string,
    private readonly hooks: ArenaHooks,
  ) {}

  enter(duelId: string): void {
    if (this.duelId === duelId && this.socket) return;
    this.duelId = duelId;
    this.attempt = 0;
    this.blocked = false;
    // A retry scheduled before the switch would fire after `connect()` below and open a second
    // socket for the same member — which the room evicts with `zamenjen`, and `zamenjen` is
    // permanent. One stale timer and the arena never reconnects again for the life of the page.
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.disconnect();
    this.connect();
  }

  leave(): void {
    this.duelId = null;
    this.disconnect();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private connect(): void {
    if (!this.duelId || this.stale || this.blocked) return;
    const url = `${this.origin.replace(/^http/, 'ws')}/api/soba/ws?dvoboj=${encodeURIComponent(
      this.duelId,
    )}&v=${SOBA_VERSION}`;

    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      this.retry();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
    };
    socket.onmessage = (event) => {
      const message = decodeFromRoom(event.data as string);
      // Unrecognised is ordinary — it is how a tab from before a feature survives a room that has
      // it. Rule 1 in `core/soba.ts`, applied in the direction that matters here.
      if (!message) return;
      switch (message.t) {
        case 'dvoboj':
          if (message.duel === this.duelId) this.hooks.onEvent(message.ev, message.side, message.axis);
          return;
        case 'soba':
          // Somebody already here when this socket opened. Announced once, like an arrival.
          for (const person of message.people) this.hooks.onPresence(person.name, true);
          return;
        case 'dolazak':
          this.hooks.onPresence(message.person.name, true);
          return;
        case 'odlazak':
          this.hooks.onPresence('', false);
          return;
        case 'kraj':
          this.hangUp(message.reason);
          return;
        default:
          return;
      }
    };
    socket.onclose = () => {
      if (this.socket === socket) this.socket = null;
      this.retry();
    };
    socket.onerror = () => {
      /* `onclose` follows and does the work. */
    };
  }

  private hangUp(reason: KrajReason): void {
    if (reason === 'verzija') this.stale = true;
    // The same distinction `live.ts` exists to make: a shut door must not be knocked on again.
    // `zamenjen` is in here because a second tab took the arena, and two tabs taking it back and
    // forth twice a second is the failure this list prevents.
    if (reason === 'zakljucano' || reason === 'van-posade' || reason === 'zamenjen')
      this.blocked = true;
  }

  private disconnect(): void {
    const socket = this.socket;
    this.socket = null;
    if (!socket) return;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    try {
      socket.close();
    } catch {
      /* already gone */
    }
  }

  private retry(): void {
    if (this.stale || this.blocked || !this.duelId) return;
    if (this.retryTimer) return;
    const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** this.attempt);
    this.attempt += 1;
    // A third of jitter, so two people whose connection dropped together do not come back together.
    const wait = base * (0.7 + Math.random() * 0.6);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, wait);
  }
}
